'use client';

import { Box, Typography } from '@mui/material';
import Nav from '@/components/Nav';
import Footer from '@/components/Footer';
import ApertureHero from '@/screens/home/components/ApertureHero';
import HowItWorksStepper from '@/screens/home/components/HowItWorksStepper';
import { PALETTE } from '@/styles/constants';

const NAV_LINKS = [
  { href: 'https://www.leaphei.com/faq.html', label: 'FAQ' },
  { href: 'https://portal-stage.leaphei.com/auth/login', label: 'Broker Login' },
];

const PRESS_LOGOS = [
  { name: 'Forbes', src: 'https://leaphei.com/images/press/forbes.jpg', tall: true },
  { name: 'Black Enterprise', src: 'https://leaphei.com/images/press/black-enterprise.svg' },
  { name: 'InvestmentNews', src: 'https://leaphei.com/images/press/investment-news.jpg' },
  { name: 'Digital Wealth News', src: 'https://leaphei.com/images/press/digital-wealth-news.png' },
  { name: 'Alternatives Watch', src: 'https://leaphei.com/images/press/alternatives-watch.png' },
  { name: 'UrbanGeekz', src: 'https://leaphei.com/images/press/urban-geekz.png' },
  {
    name: 'Wealth Solutions Report',
    src: 'https://leaphei.com/images/press/wealth-solutions-report.png',
  },
];

export default function Home() {
  return (
    <Box>
      <Nav
        links={NAV_LINKS}
        accountHref="https://app-stage.leaphei.com/auth/login?callbackUrl=%2Fverify-account"
        accountLabel="Account"
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.05fr 0.95fr' },
          minHeight: { md: '68vh' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: { xs: '40px 24px 8px', md: '40px 48px 40px 60px' },
            order: { xs: 1, md: 0 },
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: 'clamp(34px, 3.6vw, 50px)',
              lineHeight: 1.16,
              fontWeight: 400,
              color: PALETTE.ink,
              maxWidth: '420px',
            }}
          >
            Make Your Home Equity Work For You
          </Typography>
          <Box
            component="a"
            href="https://app-stage.leaphei.com/auth/login?callbackUrl=%2Fverify-account"
            sx={{
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '16px',
              marginTop: '44px',
              width: 'fit-content',
              fontSize: '13px',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: PALETTE.forest,
              textDecoration: 'none',
              padding: '21px 40px',
              background:
                'linear-gradient(155deg, #e4c989 0%, ' + PALETTE.goldLine + ' 46%, #9c7935 100%)',
              boxShadow:
                '0 1px 0 rgba(255, 255, 255, 0.4) inset, 0 14px 26px -12px rgba(28, 25, 17, 0.45)',
              overflow: 'hidden',
              transformOrigin: 'center',
              transition: 'transform 0.35s cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 0.35s ease',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-60%',
                left: '-30%',
                width: '40%',
                height: '220%',
                background:
                  'linear-gradient(120deg, transparent, rgba(255, 255, 255, 0.55), transparent)',
                transform: 'rotate(20deg) translateX(-260%)',
                transition: 'transform 0.85s ease',
              },
              '&:hover': {
                transform: 'translateY(-2px) scale(1.25)',
                boxShadow:
                  '0 1px 0 rgba(255, 255, 255, 0.5) inset, 0 24px 40px -14px rgba(28, 25, 17, 0.55)',
              },
              '&:hover::before': {
                transform: 'rotate(20deg) translateX(260%)',
              },
              '&:hover .arrow-icon': {
                transform: 'translateX(5px)',
              },
              '&:active': {
                transform: 'translateY(0) scale(1.18)',
              },
            }}
          >
            Get Started
            <Box
              component="span"
              className="arrow-icon"
              sx={{ transition: 'transform 0.3s ease', position: 'relative' }}
            >
              →
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            position: 'relative',
            padding: { xs: '24px', md: '40px 60px 40px 0' },
            display: 'flex',
            alignItems: 'stretch',
            order: { xs: 0, md: 1 },
          }}
        >
          <ApertureHero />
        </Box>
      </Box>

      <HowItWorksStepper />

      <Box
        sx={{
          padding: { xs: '32px 24px', md: '40px 60px' },
          textAlign: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: '11px',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: PALETTE.inkSoft,
            marginBottom: '24px',
          }}
        >
          As featured in
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            gap: { xs: '24px', md: '48px' },
          }}
        >
          {PRESS_LOGOS.map((logo) => (
            <Box
              key={logo.name}
              component="img"
              src={logo.src}
              alt={logo.name}
              sx={{
                height: logo.tall ? { xs: '46px', md: '54px' } : { xs: '20px', md: '24px' },
                width: 'auto',
                objectFit: 'contain',
                transition: 'transform 0.25s ease',
                '&:hover': { transform: 'scale(1.06)' },
              }}
            />
          ))}
        </Box>
      </Box>

      <Footer />
    </Box>
  );
}
